/**
 * Shared recursive traversal of a workflow's action tree.
 *
 * Logic Apps actions nest in several shapes — Scope/Foreach/Until expose a
 * child `actions` map, `If` adds an `else.actions` branch, and `Switch` fans
 * out into `cases[*].actions` plus `default.actions`. Several call sites need
 * to visit *every* action regardless of nesting, and historically each
 * re-implemented (or forgot) this walk, producing divergent behaviour — e.g.
 * mock seeding recursed while connection-mapping extraction did not.
 *
 * `walkAllActions` is the single source of truth for that traversal. The
 * visitor receives:
 *   - `names`   — candidate identifier(s) for recipe-mock lookup. For regular
 *                 actions this is `[name]`; for agent tools it is
 *                 `[toolName, innerActionName]` so a recipe can key by either.
 *   - `action`  — the concrete action object whose `inputs` should be
 *                 inspected. For tools this is the *inner* action (not the
 *                 wrapping agent) so connection extraction sees its real
 *                 `host` / `serviceProviderConfiguration`.
 *   - `scopedName` — the canvas-scoped path used as `actionName` in mocks
 *                 (`Parent/Child` for nested actions; just `innerActionName`
 *                 for tools, matching the canvas node).
 *   - `nodeId`  — the canvas node id, ready to use as a store key. Regular
 *                 actions → `action-${scopedName}`; agent tools →
 *                 `tool-${agentName}-${innerActionName}` (matches the id the
 *                 canvas builds in WorkflowDesigner / SubWorkflowPreview /
 *                 workflowStore).
 */
export function walkAllActions(
  actions: Record<string, unknown> | undefined,
  visitor: (
    names: string[],
    action: Record<string, unknown>,
    scopedName: string,
    nodeId: string,
  ) => void,
  parentPath = '',
): void {
  if (!actions) return;

  for (const [name, actionValue] of Object.entries(actions)) {
    if (!actionValue || typeof actionValue !== 'object') continue;
    const action = actionValue as Record<string, unknown>;
    const scopedName = parentPath ? `${parentPath}/${name}` : name;

    visitor([name], action, scopedName, `action-${scopedName}`);

    // Scope / Foreach / Until — direct child actions.
    if (action.actions && typeof action.actions === 'object') {
      walkAllActions(action.actions as Record<string, unknown>, visitor, scopedName);
    }
    // If — the else branch.
    if (action.else && typeof action.else === 'object') {
      const elseBranch = action.else as Record<string, unknown>;
      if (elseBranch.actions && typeof elseBranch.actions === 'object') {
        walkAllActions(elseBranch.actions as Record<string, unknown>, visitor, scopedName);
      }
    }
    // Switch — each case branch.
    if (action.cases && typeof action.cases === 'object') {
      for (const caseValue of Object.values(action.cases as Record<string, unknown>)) {
        if (!caseValue || typeof caseValue !== 'object') continue;
        const typedCase = caseValue as Record<string, unknown>;
        if (typedCase.actions && typeof typedCase.actions === 'object') {
          walkAllActions(typedCase.actions as Record<string, unknown>, visitor, scopedName);
        }
      }
    }
    // Switch — the default branch.
    if (action.default && typeof action.default === 'object') {
      const defaultBranch = action.default as Record<string, unknown>;
      if (defaultBranch.actions && typeof defaultBranch.actions === 'object') {
        walkAllActions(defaultBranch.actions as Record<string, unknown>, visitor, scopedName);
      }
    }

    // Agent tools — nested under the agent's `tools` property. Each tool wraps
    // one or more inner actions and is rendered on canvas as a single tool
    // node with id `tool-{agentName}-{innerActionName}` (the canvas uses the
    // first inner action's name, falling back to the tool name).
    if (action.tools && typeof action.tools === 'object') {
      const agentName = name; // tool node ids use the bare agent name, not scopedName
      for (const [toolName, toolValue] of Object.entries(action.tools as Record<string, unknown>)) {
        if (!toolValue || typeof toolValue !== 'object') continue;
        const tool = toolValue as Record<string, unknown>;
        const toolActions = tool.actions as Record<string, unknown> | undefined;
        const innerEntries = toolActions ? Object.entries(toolActions) : [];
        const [innerActionName, innerActionValue] = innerEntries[0] ?? [toolName, undefined];
        // Inspect the inner action (where the real connection / inputs live),
        // not the wrapping agent. Falls back to an empty object if the tool
        // declared no inner actions.
        const innerAction =
          innerActionValue && typeof innerActionValue === 'object'
            ? (innerActionValue as Record<string, unknown>)
            : ({} as Record<string, unknown>);

        visitor(
          [toolName, innerActionName],
          innerAction,
          innerActionName,
          `tool-${agentName}-${innerActionName}`,
        );
      }
    }
  }
}
